import React, { useState, useEffect, useRef } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Tooltip } from "../ui/tooltip";
import { Spinner } from "../ui/spinner";
import { cn } from "../../lib/utils";
import { SearchService } from "../../services/SearchService";
import { usePermissions } from "../../PermissionsProvider";
import SessionOverview from "../Session/SessionOverview";
import { Session } from "../../model_gen/session";

interface Category {
  name: string;
  key: string;
}

interface SearchProps {
  disabled?: boolean;
}

const Search: React.FC<SearchProps> = ({ disabled = false }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { apiKey } = usePermissions();
  const categories: Category[] = [
    { name: "Sessions", key: "sessions" },
    { name: "Requests", key: "requests" },
  ];
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [currentCategory, setCurrentCategory] = useState<Category | null>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [sessionModalVisible, setSessionModalVisible] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    setShowSuggestions(searchTerm.length > 0);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setCurrentCategory(null);
        setSearchResults([]);
        setSearchTerm("");
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchTerm, currentCategory]);

  const handleSearch = (category: Category) => {
    setCurrentCategory(category);
    SearchService.search(
      category.key,
      searchTerm,
      setIsSearching,
      apiKey,
    ).subscribe(
      (data: any) => {
        if (!data || data.length === 0) {
          setSearchResults([{ isEmpty: true, message: "No results found" }]);
        } else {
          setSearchResults(data);
        }
        setShowSuggestions(true);
      },
      (error: any) => {
        console.error(error);
      },
    );
  };

  const handleResultClick = (result: any) => {
    setSelectedRequestId(result.request_id || null);
    setSelectedSession({
      application_id: result.application_id,
      session_id: result.session_id,
    });

    setSessionModalVisible(true);
    setRefreshTrigger((prev) => prev + 1);
    setShowSuggestions(false);
    setSearchTerm("");
    setCurrentCategory(null);
    setSearchResults([]);
  };

  const handleSessionModalClose = () => {
    setSelectedSession(null);
    setSessionModalVisible(false);
  };

  const renderSearchResult = (result: any, index: number) => {
    const commonProps = {
      key: index,
      className: cn(
        "px-4 py-3 cursor-pointer text-sm border-b border-border last:border-b-0 hover:bg-accent transition-colors",
        result.isEmpty && "cursor-default hover:bg-transparent"
      ),
      onClick: (e: React.MouseEvent) => {
        if (result.isEmpty) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        handleResultClick(result);
      },
    };

    if (result.isEmpty) {
      return (
        <li {...commonProps}>
          <span className="text-muted-foreground">{result.message}</span>
        </li>
      );
    }

    if (result.request_id) {
      return (
        <li {...commonProps}>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">
              {result.application_name} / {result.session_id} /
            </span>
            <span className="text-foreground font-medium">{result.request_id}</span>
          </div>
        </li>
      );
    } else if (result.session_id) {
      return (
        <li {...commonProps}>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">
              {result.application_name} /
            </span>
            <span className="text-foreground font-medium">{result.session_id}</span>
          </div>
        </li>
      );
    }
    return null;
  };

  const searchInput = (
    <div className="relative w-full">
      <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
      <Input
        ref={inputRef}
        className="pl-10 pr-10"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setCurrentCategory(null);
          setSearchResults([]);
        }}
        disabled={disabled}
      />
      {isSearching && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <Spinner size="sm" className="text-muted-foreground" />
        </div>
      )}
    </div>
  );

  return (
    <div className="relative w-full max-w-md">
      {disabled ? (
        <Tooltip content="Make a request using the Maitai SDK to use this feature">
          {searchInput}
        </Tooltip>
      ) : (
        searchInput
      )}
      
      {showSuggestions && !disabled && (
        <ul 
          ref={suggestionsRef} 
          className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-lg z-50 max-h-80 overflow-y-auto"
        >
          {currentCategory
            ? searchResults.map((result, index) =>
                renderSearchResult(result, index),
              )
            : categories.map((category, index) => (
                <li
                  key={index}
                  className="px-4 py-3 cursor-pointer text-sm border-b border-border last:border-b-0 hover:bg-accent transition-colors flex items-center justify-between"
                  onClick={() => handleSearch(category)}
                >
                  <span className="text-foreground">{searchTerm}</span>
                  <span className="text-muted-foreground text-xs">in {category.name}</span>
                </li>
              ))}
        </ul>
      )}
      
      <Dialog open={sessionModalVisible} onOpenChange={setSessionModalVisible}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Session Details</DialogTitle>
          </DialogHeader>
          {selectedSession && (
            <SessionOverview
              sessionId={selectedSession.session_id}
              applicationId={selectedSession.application_id.toString()}
              refreshTrigger={refreshTrigger}
              requestId={selectedRequestId || undefined}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Search;
